import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { LIMITS } from '@vybe/shared';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { useI18n } from '@/locales';
import { useSessionStore } from '@/stores/session.store';

/** Creates the guest identity (server-issued id + token). */
export function NameForm({ onDone, submitLabel }: { onDone?: () => void; submitLabel?: string }) {
  const { m } = useI18n();
  const register = useSessionStore((s) => s.register);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setFailed(false);
    try {
      await register(name.trim());
      onDone?.();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="display-name">{m.name.label}</Label>
        <Input
          id="display-name"
          autoFocus
          maxLength={LIMITS.displayNameMax}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={m.name.placeholder}
          autoComplete="nickname"
        />
      </div>
      {failed ? (
        <p role="alert" className="text-sm text-danger">
          {m.room.errors.unknown.text}
        </p>
      ) : null}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="w-full"
        disabled={!name.trim() || busy}
      >
        {busy ? <Loader2 className="animate-spin" /> : null}
        {submitLabel ?? m.name.continue}
      </Button>
    </form>
  );
}
